'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import { cva, VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { ComponentProps } from 'react'

import { cn } from '@/shared/lib'

import { Icon } from '../icon'

const contentVariants = cva('', {
  variants: {
    color: {
      white: 'bg-white fill-white text-black',
      black: 'bg-gray-900 fill-gray-900 text-white',
      purple: 'bg-purple-500 fill-purple-500 text-white',
      blue: 'bg-blue-500 fill-blue-500 text-white',
      red: 'bg-red-500 fill-red-500 text-white',
    },
    closeButton: {
      true: 'pr-8',
      false: '',
    },
  },
})

export const Root = ({
  ...props
}: ComponentProps<typeof PopoverPrimitive.Root>) => {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

export const Trigger = ({
  ...props
}: ComponentProps<typeof PopoverPrimitive.Trigger>) => {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

type ContentProps = ComponentProps<typeof PopoverPrimitive.Content> &
  VariantProps<typeof contentVariants> & {
    arrow?: boolean
    closeButton?: boolean
  }

export const Content = ({
  color = 'white',
  side = 'top',
  sideOffset = 0,
  align = 'center',
  alignOffset = 0,
  arrow = true,
  arrowPadding = 0,
  closeButton = true,
  className,
  children,
  ...props
}: ContentProps) => {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        className={cn(
          'typo-caption1 z-tooltip rounded-md p-2 font-medium text-balance outline-hidden',
          'drop-shadow-[0px_0px_4px_rgba(0,0,0,0.12),2px_6px_12px_rgba(0,0,0,0.12)] filter',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2',
          'data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2',
          'data-[side=top]:slide-in-from-bottom-2',
          contentVariants({ color, closeButton }),
          className,
        )}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        arrowPadding={arrowPadding}
        {...props}
      >
        {children}
        {closeButton && (
          <PopoverPrimitive.Close
            className={cn(
              'absolute top-2 right-2 inline-flex size-4 cursor-pointer items-center justify-center rounded-full outline-none',
              contentVariants({ color }),
            )}
            aria-label="Close"
          >
            <Icon name="close" size={16} />
          </PopoverPrimitive.Close>
        )}
        {arrow && (
          <PopoverPrimitive.Arrow
            height={7}
            className={cn(
              contentVariants({ color }),
              'bg-transparent',
              '-translate-y-px',
            )}
          />
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

export const Anchor = ({
  ...props
}: ComponentProps<typeof PopoverPrimitive.Anchor>) => {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

export const Close = ({
  ...props
}: ComponentProps<typeof PopoverPrimitive.Close>) => {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />
}
